import { useCallback } from 'react';
import { useGameStateContext } from '../GameStateProvider';
import { parseSeededData, generateAutoSeed } from '../utils/seedUtils';

export const useSeedActions = () => {
  const { state, dispatch } = useGameStateContext();

  const handleStartSeeded = useCallback((seed: string) => {
    if (!seed || !seed.trim()) {
      dispatch({ 
        type: "SET_HUD_HINT", 
        hint: "Please enter a valid seed" 
      });
      setTimeout(() => {
        dispatch({ type: "CLEAR_HUD_HINT" });
      }, 2000);
      return;
    }

    try {
      const seedData = parseSeededData(seed.trim(), state.activeSeason?.item || '');
      if (seedData) {
        dispatch({ type: "SET_CUSTOM_SEED", seed: seed.trim() });
        dispatch({ type: "APPLY_SEED", seedData });
        dispatch({ 
          type: "SET_HUD_HINT", 
          hint: "Seed applied successfully!" 
        });
        setTimeout(() => {
          dispatch({ type: "CLEAR_HUD_HINT" });
        }, 2000);
      } else {
        throw new Error("Invalid seed data");
      }
    } catch (error) {
      console.error("Failed to apply seed:", error);
      dispatch({ 
        type: "SET_HUD_HINT", 
        hint: "Invalid seed format" 
      });
      setTimeout(() => {
        dispatch({ type: "CLEAR_HUD_HINT" });
      }, 2000);
    }
  }, [dispatch, state.activeSeason]);

  const handleCopyShareCode = useCallback(async () => {
    try {
      const autoSeed = generateAutoSeed({
        level: state.level,
        discoveredRecipes: state.discoveredRecipes,
        coins: state.coins,
        xp: state.xp,
        prestigeLevel: state.prestigeLevel,
        activeSeason: state.activeSeason?.type || null,
        inventory: state.inventory,
        reputation: state.reputation
      });

      const url = `${window.location.origin}${window.location.pathname}?seed=${encodeURIComponent(autoSeed)}`;
      
      await navigator.clipboard.writeText(url);
      dispatch({ 
        type: "SET_HUD_HINT", 
        hint: "Share link copied to clipboard!" 
      });
    } catch (err) {
      const autoSeed = generateAutoSeed({
        level: state.level,
        discoveredRecipes: state.discoveredRecipes,
        coins: state.coins,
        xp: state.xp,
        prestigeLevel: state.prestigeLevel,
        activeSeason: state.activeSeason?.type || null,
        inventory: state.inventory,
        reputation: state.reputation
      });

      const url = `${window.location.origin}${window.location.pathname}?seed=${encodeURIComponent(autoSeed)}`;
      
      const textArea = document.createElement("textarea");
      textArea.value = url;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.select();
      
      try {
        document.execCommand("copy");
        dispatch({ 
          type: "SET_HUD_HINT", 
          hint: "Share link copied to clipboard!" 
        });
      } catch (fallbackErr) {
        dispatch({ 
          type: "SET_HUD_HINT", 
          hint: `Copy this link: ${url}` 
        });
      } finally {
        document.body.removeChild(textArea);
      }
    }
    
    setTimeout(() => {
      dispatch({ type: "CLEAR_HUD_HINT" });
    }, 3000);
  }, [state, dispatch]);

  return { handleStartSeeded, handleCopyShareCode };
};
